// assets/order-tracking-center.js
// Notes (Return logic - BE driven):
// - "Return in progress" is a return workflow status (NOT the same as refund).
// - Return UI is BE-driven: FE only reflects what BE returns in the order payload.
// - We do NOT auto-refresh the page; refund/exchange UI updates only when BE closes the return
//   (or when an exchange shipment is created) and the order payload is fetched again.
// - Refund rows show "Payment status: Refunded" only when BE provides refund data.
// - IMPORTANT: If the same SKU has refunded qty across multiple returns with different statuses (OPEN + CLOSED),
//   FE must split into multiple refunded rows to show correct labels.

const CURRENCY_SYMBOL_MAPPING = {
  HKD: 'HK${{amount}}',
  USD: '${{amount}} USD',
  JPY: '¥{{amount}} JPY',
  SGD: '${{amount}} SGD',
  TWD: '${{amount}} TWD',
  GBP: '£{{amount}} GBP',
  EUR: '€{{amount}} EUR',
  AUD: '${{amount}} AUD',
  CAD: '${{amount}} CAD',
  NZD: '${{amount}} NZD',
};

// Global state
let currentOrderData = null;
let orderNumber = null;
let customerEmail = null;

// Keep order currency (best effort)
let currentOrderCurrencyCode = null;

const heading = document.querySelector('.page-header h1');

document.addEventListener('DOMContentLoaded', () => {
  initializeOrderTrackingCenter();
});

// ✅ prefer presentmentMoney (order currency), fallback shopMoney
function pickMoneyFromSet(moneySet) {
  if (!moneySet) return null;
  return moneySet.presentmentMoney || moneySet.shopMoney || null;
}

function getOrderCurrencyCode(orderData) {
  // 1) Prefer from order totals
  const m =
    pickMoneyFromSet(orderData?.currentTotalPriceSet) ||
    pickMoneyFromSet(orderData?.totalPriceSet) ||
    null;

  if (m?.currencyCode) return m.currencyCode;

  // 2) Fallback: scan lineItems originalUnitPriceSet
  const lineItems = orderData?.lineItems?.nodes || [];
  for (const it of lineItems) {
    const mm = pickMoneyFromSet(it?.originalUnitPriceSet);
    if (mm?.currencyCode) return mm.currencyCode;
  }

  // 3) Fallback: scan fulfillment line items
  const fulfillments = orderData?.fulfillments || [];
  for (const ff of fulfillments) {
    const nodes = ff?.fulfillmentLineItems?.nodes || [];
    for (const n of nodes) {
      const mm = pickMoneyFromSet(n?.lineItem?.originalUnitPriceSet);
      if (mm?.currencyCode) return mm.currencyCode;
    }
  }

  return orderData?.presentmentCurrencyCode || orderData?.currencyCode || null;
}

function initializeOrderTrackingCenter() {
  setupEventListeners();
}

function setupEventListeners() {
  const orderLookupForm = document.getElementById('order-tracking-form');
  if (orderLookupForm) {
    orderLookupForm.addEventListener('submit', handleOrderLookup);
  }

  document.addEventListener('click', function (e) {
    if (e.target.matches('.contact-us-btn')) handleContactUs();
    if (e.target.classList.contains('track-shipment-btn')) handleTrackShipment(e.target);
  });
}

async function handleOrderLookup(e) {
  e.preventDefault();
  document.body.classList.remove('is-order-canceled');
  hideError();

  orderNumber = document.getElementById('order_number')?.value?.trim();
  customerEmail = document.getElementById('customer_email')?.value?.trim();
  const t = window.orderTrackingConfig?.translation || {};

  if (!orderNumber || !customerEmail) {
    showError(t.errorRequiredFields || 'Please fill in all required fields');

    return;
  }
  if (!isValidEmail(customerEmail)) {
    showError(t.errorInvalidEmail || 'Email is invalid');
    return;
  }

  if (
    orderNumber.startsWith('EVISTAG') ||
    orderNumber.startsWith('EVIHK') ||
    orderNumber.startsWith('EVIJP') ||
    orderNumber.startsWith('EVPJP')
  ) {
    document.body.classList.add('order-3-steps');
  } else {
    document.body.classList.remove('order-3-steps');
  }

  const config = window.orderTrackingConfig || {};
  const mwUrl = config.mwAddress;
  const storeName = config.storeName;

  if (!mwUrl) return showError(t.middlewareNotConfigured || 'Middleware URL not configured');
  if (!storeName) return showError(t.storeNameNotConfigured || 'Store name not configured');

  try {
    const response = await fetch(`${mwUrl.endsWith('/') ? mwUrl : mwUrl + '/'}getOrderData`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeName, orderName: orderNumber, email: customerEmail }),
    });

    if (!response.ok) {
      showError(t.errorFetching || 'An error occurred while fetching order data');
      return;
    }

    const result = await response.json();

    if (!result.success) {
      showError(t.errorNotFound || 'Order not found or there was an error fetching order data');
      return;
    }

    currentOrderData = result.data;
    currentOrderCurrencyCode = getOrderCurrencyCode(currentOrderData);

    displayOrderTracking(currentOrderData);

    document.getElementById('order-lookup-section').style.display = 'none';
    document.getElementById('order-tracking-section').style.display = 'block';
    if (heading) heading.style.display = 'none';
  } catch (error) {
    showError(t.errorFetching || 'An error occurred while fetching order data');
    console.error(error);
  }
}

function displayOrderTracking(orderData) {
  displayOrderHeader(orderData);
  displayOrderStatus(orderData);
  displayShipments(orderData);
  // ✅ Removed: displayOrderSummary(orderData);
}

function displayOrderHeader(orderData) {
  const t = window.orderTrackingConfig.translation;
  const orderHeaderContainer = document.getElementById('order-header');
  const createdAt = orderData.processedAt || orderData.createdAt;

  orderHeaderContainer.innerHTML = `
    <div class="order-header-content">
      <h1>${t.order} #${orderNumber}</h1>
      <p class="order-date">${t.orderDate}: ${formatDate(createdAt)}</p>
    </div>
  `;
}

function displayOrderStatus(orderData) {
  const t = window.orderTrackingConfig.translation;
  const fin = orderData.displayFinancialStatus;
  const ful = orderData.displayFulfillmentStatus;
  const tags = orderData.tags || [];
  const fulfillments = orderData.fulfillments || [];

  let currentKey, title, message;

  if (orderData.cancelledAt || fin === 'VOIDED' || (fin === 'REFUNDED' && ful === 'UNFULFILLED')) {
    currentKey = 'canceled';
    title = t.canceled_title;
    message = t.canceled_message;
  } else if (ful === 'FULFILLED') {
    currentKey = 'shipped';
    title = t.shipped_title;
    message = t.shipped_message;
  } else if (ful === 'PARTIALLY_FULFILLED') {
    currentKey = 'partiallyShipped';
    title = t.partially_shipped_title;
    message = t.partially_shipped_message;
  } else if (ful === 'UNFULFILLED' && tags.includes('OOS')) {
    currentKey = 'warehouseTransferOOS';
    title = t.warehouse_transfer_oos_title;
    message = t.warehouse_transfer_oos_message;
  } else if (ful === 'UNFULFILLED' && tags.includes('order_delay')) {
    currentKey = 'warehouseTransferDelay';
    title = t.warehouse_transfer_title;
    message = t.warehouse_transfer_delay_message;
  } else if (ful === 'UNFULFILLED' && tags.includes('fraud_check')) {
    currentKey = 'paymentVerification';
    title = t.payment_verification_title;
    message = t.payment_verification_message;
  } else {
    currentKey = 'processing';
    title = t.order_processing_title;
    message = t.order_processing_message;
  }

  const isCanceled = currentKey === 'canceled';
  document.body.classList.toggle('is-order-canceled', isCanceled);

  let pipelineStage = 'processing';
  if (['partiallyShipped', 'shipped'].includes(currentKey)) pipelineStage = 'in_transit';

  const allDelivered =
    orderData.displayFulfillmentStatus === 'FULFILLED' &&
    fulfillments.length > 0 &&
    fulfillments.every((ff) => ff.events?.nodes?.some((e) => e.status === 'DELIVERED'));

  if (allDelivered) pipelineStage = 'delivered';

  const successFulfillments = (orderData.fulfillments || []).filter((f) => f.status === 'SUCCESS');

  let trackButtonsHtml = '';
  successFulfillments.forEach((ff, idx) => {
    const url = ff.trackingInfo?.[0]?.url || '';
    if (!url) return;
    const shipmentId = idx + 1;
    trackButtonsHtml += `
      <button class="btn-primary track-shipment-btn" data-shipment="${shipmentId}" data-url="${url}">
        ${t.trackShipment} #${shipmentId}
      </button>
    `;
  });

  const contactButtonHtml = `
    <button class="btn-primary contact-us-btn">
      ${t.contactUs}
    </button>
  `;

  const isOOS = currentKey === 'warehouseTransferOOS';
  const titleHtml = String(title || '').replace(/\n/g, '<br>');
  document.getElementById('order-status').innerHTML = `
    <div class="order-status-progress">
      <div class="status-steps">
        <div class="status-step ${['confirmed','processing','in_transit','delivered'].includes(pipelineStage) ? 'completed' : ''} ${pipelineStage==='confirmed'?'current':''}">
          <div class="step-circle"></div>
          <span class="step-label">${t.confirmed}</span>
        </div>
        <div class="status-step ${['processing','in_transit','delivered'].includes(pipelineStage) ? 'completed' : ''} ${pipelineStage==='processing'?'current':''}">
          <div class="step-circle"></div>
          <span class="step-label">${t.processing}</span>
        </div>
        <div class="status-step in_transit ${['in_transit','delivered'].includes(pipelineStage) ? 'completed' : ''} ${pipelineStage==='in_transit'?'current':''}">
          <div class="step-circle"></div>
          <span class="step-label in-transit">${t.inTransit}</span>
          <span class="step-label shipped">${t.shipped}</span>
        </div>
        <div class="status-step delivered ${pipelineStage==='delivered' ? 'completed current' : ''}">
          <div class="step-circle"></div>
          <span class="step-label">${t.delivered}</span>
        </div>
      </div>
    </div>

    <div class="current-status${isOOS ? ' oos' : ''}">
      <h2>${titleHtml}</h2>
      <p>${message}</p>
    </div>

    <div class="action-buttons">
      ${trackButtonsHtml}
      ${contactButtonHtml}
    </div>
  `;
}

// ---------------- RETURN helpers (KEY-based) ----------------
function normalizeStatus(s) {
  return String(s || '').trim().toUpperCase().replace(/\s+/g, '_');
}

/** keep sku helper for display + fallback */
function getSkuFromAny(obj) {
  return (
    obj?.variant?.sku ||
    obj?.variant?.magentoSku?.value ||
    obj?.lineItem?.variant?.sku ||
    obj?.lineItem?.sku ||
    obj?.fulfillmentLineItem?.lineItem?.variant?.sku ||
    obj?.fulfillmentLineItem?.lineItem?.sku ||
    obj?.sku ||
    ''
  );
}

function getLineItemIdFromAny(obj) {
  return (
    obj?.id ||
    obj?.lineItem?.id ||
    obj?.fulfillmentLineItem?.lineItem?.id ||
    obj?.node?.lineItem?.id ||
    ''
  );
}

/**
 * ✅ Key chuẩn cho mọi map:
 * - Prefer lineItem.id
 * - Fallback variant.id
 * - Fallback SKU
 * - Fallback cuối: title + unit price
 */
function getAnyItemKey(obj) {
  const liId = getLineItemIdFromAny(obj);
  if (liId) return `li:${liId}`;

  const variantId =
    obj?.variant?.id ||
    obj?.lineItem?.variant?.id ||
    obj?.fulfillmentLineItem?.lineItem?.variant?.id ||
    '';
  if (variantId) return `v:${variantId}`;

  const sku = getSkuFromAny(obj);
  if (sku) return `sku:${sku}`;

  const title = obj?.title || obj?.lineItem?.title || '';
  const m = pickMoneyFromSet(obj?.originalUnitPriceSet || obj?.lineItem?.originalUnitPriceSet);
  const amt = m?.amount ?? '';
  const cc = m?.currencyCode ?? '';
  return `tmp:${String(title)}|${String(amt)}|${String(cc)}`;
}

/** Accepts multiple shapes from middleware/Shopify-like payloads. */
function getListEdgesOrNodes(conn) {
  if (!conn) return [];
  if (Array.isArray(conn)) return conn;
  if (Array.isArray(conn.nodes)) return conn.nodes;
  if (Array.isArray(conn.edges)) return conn.edges.map((e) => e?.node).filter(Boolean);
  return [];
}

function buildReturnInProgressQtyMap(orderData) {
  const map = {};

  const returnsNodes =
    orderData?.returns?.nodes ||
    orderData?.returns ||
    orderData?.returnDetails?.nodes ||
    orderData?.returnDetails ||
    [];

  const returnsList = Array.isArray(returnsNodes) ? returnsNodes : [];

  returnsList.forEach((ret) => {
    const st = normalizeStatus(ret?.status || ret?.returnStatus || ret?.state);
    const isInProgress =
      st === 'RETURN_IN_PROGRESS' || st === 'IN_PROGRESS' || st === 'OPEN' || st === 'REQUESTED';
    if (!isInProgress) return;

    const items =
      getListEdgesOrNodes(ret?.returnLineItems) ||
      getListEdgesOrNodes(ret?.lineItems) ||
      getListEdgesOrNodes(ret?.items) ||
      [];

    items.forEach((node) => {
      const key = getAnyItemKey(node);
      const qty = Number(node?.quantity || node?.returnedQuantity || 0) || 0;
      if (!key || qty <= 0) return;
      map[key] = (map[key] || 0) + qty;
    });
  });

  return map;
}

function buildReturnClosedQtyMap(orderData) {
  const map = {};

  const returnsNodes =
    orderData?.returns?.nodes ||
    orderData?.returns ||
    orderData?.returnDetails?.nodes ||
    orderData?.returnDetails ||
    [];

  const returnsList = Array.isArray(returnsNodes) ? returnsNodes : [];

  returnsList.forEach((ret) => {
    const st = normalizeStatus(ret?.status || ret?.returnStatus || ret?.state);
    const isClosed = st === 'CLOSED' || st === 'COMPLETED' || st === 'COMPLETE' || st === 'RESOLVED';
    if (!isClosed) return;

    const items =
      getListEdgesOrNodes(ret?.returnLineItems) ||
      getListEdgesOrNodes(ret?.lineItems) ||
      getListEdgesOrNodes(ret?.items) ||
      [];

    items.forEach((node) => {
      const key = getAnyItemKey(node);
      const qty = Number(node?.quantity || node?.returnedQuantity || 0) || 0;
      if (!key || qty <= 0) return;
      map[key] = (map[key] || 0) + qty;
    });
  });

  return map;
}

function orderHasPostPurchaseEvents(orderData) {
  const hasRefund = (orderData?.refunds || []).length > 0;
  const hasReturn = (orderData?.returns?.nodes || orderData?.returns || []).length > 0;
  return hasRefund || hasReturn;
}

function displayShipments(orderData) {
  const t = window.orderTrackingConfig.translation;
  const shipmentsContainer = document.getElementById('shipments-section');
  const lineItems = orderData.lineItems?.nodes || [];

  // Refund qty map by KEY (ONLY count if refund actually happened)
  const refundedQtyMap = {};
  (orderData.refunds || []).forEach((refund) => {
    const refundedAmount = (refund.refundLineItems?.edges || []).reduce((sum, e) => {
      const a =
        Number(e?.node?.subtotalSet?.shopMoney?.amount || 0) ||
        Number(e?.node?.subtotalSet?.presentmentMoney?.amount || 0);
      return sum + a;
    }, 0);

    if (refundedAmount <= 0) return;

    (refund.refundLineItems?.edges || []).forEach((edge) => {
      const node = edge?.node;
      const key = getAnyItemKey(node?.lineItem || node);
      const qty = Number(node?.quantity || 0) || 0;
      if (!key || qty <= 0) return;
      refundedQtyMap[key] = (refundedQtyMap[key] || 0) + qty;
    });
  });

  // Return qty maps (KEY)
  const returnInProgressQtyMap = buildReturnInProgressQtyMap(orderData);
  const returnClosedQtyMap = buildReturnClosedQtyMap(orderData);

  const returnOpenRemaining = { ...returnInProgressQtyMap };
  const returnClosedRemaining = { ...returnClosedQtyMap };

  // Clamp map by KEY: prefer currentQuantity
  const itemKeyCurrentQtyMap = {};
  lineItems.forEach((it) => {
    const key = getAnyItemKey(it);
    if (!key) return;

    const currentQty = Number(it?.currentQuantity);
    const hasCurrent = Number.isFinite(currentQty) && currentQty >= 0;

    const qty = Number(it?.quantity || 0) || 0;
    const useQty = hasCurrent ? currentQty : qty;

    itemKeyCurrentQtyMap[key] = (itemKeyCurrentQtyMap[key] || 0) + useQty;
  });

  const itemShipRemaining = { ...itemKeyCurrentQtyMap };

  const processingItems = lineItems.filter((item) => (Number(item.unfulfilledQuantity) || 0) > 0);
  const fulfillmentsSuccess = (orderData.fulfillments || []).filter((f) => f.status === 'SUCCESS');

  if (fulfillmentsSuccess.length === 0 && processingItems.length === 0) {
    shipmentsContainer.innerHTML = '';
    return;
  }

  const refundedRemaining = { ...refundedQtyMap };

  // Apply refunds to CANCELLED fulfillments first to avoid double counting
  (orderData.fulfillments || [])
    .filter((f) => f.status === 'CANCELLED')
    .forEach((f) => {
      (f.fulfillmentLineItems?.nodes || []).forEach((node) => {
        const key = getAnyItemKey(node?.lineItem || node);
        const shippedQty = Number(node.quantity) || 0;
        const toApply = Math.min(shippedQty, refundedRemaining[key] || 0);
        if (toApply > 0) refundedRemaining[key] -= toApply;
      });
    });

  let shipmentsHTML = `<h3>${t.orderSummary}</h3>`;

  // SUCCESS fulfillments (shipments)
  fulfillmentsSuccess.forEach((fulfillment, idx) => {
    const tracking = fulfillment.trackingInfo?.[0] || {};

    const shippedItems = (fulfillment.fulfillmentLineItems?.nodes || [])
      .flatMap((node) => {
        const key = getAnyItemKey(node?.lineItem || node);

        const shippedQtyRaw = Number(node.quantity) || 0;
        if (!key || shippedQtyRaw <= 0) return [];

        // qty gốc của line item (để tính proration đúng sau khi split row)
        const baseQtyForMath = Math.max(1, toNum(node?.lineItem?.quantity || shippedQtyRaw || 1));

        const liCurrentQty = Number(node?.lineItem?.currentQuantity);
        const hasLiCurrent = Number.isFinite(liCurrentQty) && liCurrentQty >= 0;

        const hasReturnOrRefund =
          (returnClosedQtyMap[key] || 0) > 0 ||
          (returnInProgressQtyMap[key] || 0) > 0 ||
          (refundedQtyMap[key] || 0) > 0;

        let shippedQty = shippedQtyRaw;

        if (hasLiCurrent && liCurrentQty > 0 && !hasReturnOrRefund) {
          shippedQty = Math.min(shippedQtyRaw, liCurrentQty);
        } else if (!hasLiCurrent && itemShipRemaining[key] != null) {
          shippedQty = Math.min(shippedQtyRaw, Number(itemShipRemaining[key] || 0));
          itemShipRemaining[key] = Math.max(0, Number(itemShipRemaining[key] || 0) - shippedQty);
        }

        if (shippedQty <= 0) return [];

        // 1) Split by REFUND
        const refundable = refundedRemaining[key] || 0;
        const refundQty = Math.min(shippedQty, refundable);
        const keepQty = shippedQty - refundQty;

        if (refundQty > 0) refundedRemaining[key] = refundable - refundQty;

        const rows = [];

        // A) REFUND rows consume RETURN qty FIRST (CLOSED -> OPEN)
        if (refundQty > 0) {
          let remainingRefund = refundQty;

          // A1) Refunded + Return completed (CLOSED) ✅ show refunded here
          const closedAvail = returnClosedRemaining[key] || 0;
          const closedQty = Math.min(remainingRefund, closedAvail);
          if (closedQty > 0) {
            returnClosedRemaining[key] = closedAvail - closedQty;
            remainingRefund -= closedQty;
            rows.push({
              ...node.lineItem,
              quantity: closedQty,
              _baseLineItemQty: baseQtyForMath,
              originalUnitPriceSet: node.lineItem.originalUnitPriceSet,
              paymentStatusRefunded: true,
              returnLabelType: 'CLOSED',
            });
          }

          // A2) Refunded + Return in progress (OPEN) (no refunded label per requirement)
          const openAvail = returnOpenRemaining[key] || 0;
          const openQty = Math.min(remainingRefund, openAvail);
          if (openQty > 0) {
            returnOpenRemaining[key] = openAvail - openQty;
            remainingRefund -= openQty;
            rows.push({
              ...node.lineItem,
              quantity: openQty,
              _baseLineItemQty: baseQtyForMath,
              originalUnitPriceSet: node.lineItem.originalUnitPriceSet,
              paymentStatusRefunded: false,
              returnLabelType: 'IN_PROGRESS',
            });
          }

          // A3) fallback refunded (no return qty left)
          if (remainingRefund > 0) {
            rows.push({
              ...node.lineItem,
              quantity: remainingRefund,
              _baseLineItemQty: baseQtyForMath,
              originalUnitPriceSet: node.lineItem.originalUnitPriceSet,
              paymentStatusRefunded: true,
              returnLabelType: null,
            });
          }
        }

        // B) KEEP rows: apply remaining RETURN qty, then normal keep
        if (keepQty > 0) {
          let keepRemaining = keepQty;

          const closedAvailKeep = returnClosedRemaining[key] || 0;
          const closedQtyKeep = Math.min(keepRemaining, closedAvailKeep);
          if (closedQtyKeep > 0) {
            returnClosedRemaining[key] = closedAvailKeep - closedQtyKeep;
            keepRemaining -= closedQtyKeep;
            rows.push({
              ...node.lineItem,
              quantity: closedQtyKeep,
              _baseLineItemQty: baseQtyForMath,
              originalUnitPriceSet: node.lineItem.originalUnitPriceSet,
              paymentStatusRefunded: false,
              returnLabelType: 'CLOSED',
            });
          }

          const openAvailKeep = returnOpenRemaining[key] || 0;
          const openQtyKeep = Math.min(keepRemaining, openAvailKeep);
          if (openQtyKeep > 0) {
            returnOpenRemaining[key] = openAvailKeep - openQtyKeep;
            keepRemaining -= openQtyKeep;
            rows.push({
              ...node.lineItem,
              quantity: openQtyKeep,
              _baseLineItemQty: baseQtyForMath,
              originalUnitPriceSet: node.lineItem.originalUnitPriceSet,
              paymentStatusRefunded: false,
              returnLabelType: 'IN_PROGRESS',
            });
          }

          if (keepRemaining > 0) {
            rows.push({
              ...node.lineItem,
              quantity: keepRemaining,
              _baseLineItemQty: baseQtyForMath,
              originalUnitPriceSet: node.lineItem.originalUnitPriceSet,
              paymentStatusRefunded: false,
              returnLabelType: null,
            });
          }
        }

        return rows;
      })
      .filter((item) => (Number(item?.quantity) || 0) > 0);

    if (shippedItems.length === 0) return;

    shipmentsHTML += `
      <div class="shipment-card">
        <div class="shipment-header"><h4>${t.shipment} #${idx + 1}</h4></div>
        <div class="shipment-items">
          ${shippedItems.map((item) => generateItemHTML(item, t)).join('')}
        </div>
        <div class="shipment-details">
          <div class="tracking-info">
            <p>${t.carrier}: <strong>${tracking.company || '—'}</strong></p>
            <p>${t.trackingNumber}: <strong>${tracking.number || '—'}</strong></p>
          </div>
        </div>
      </div>
    `;
  });

  // Processing (unfulfilled) items
  if (processingItems.length > 0) {
    shipmentsHTML += `
      <div class="shipment-card">
        <div class="shipment-header"><h4>${t.processing}</h4></div>
        <div class="shipment-items">
          ${processingItems
            .flatMap((item) => {
              const key = getAnyItemKey(item);
              const unfulfilledQty = Number(item.unfulfilledQuantity) || 0;
              if (!key || unfulfilledQty <= 0) return [];

              let remaining = unfulfilledQty;
              const rows = [];

              const closedAvail = returnClosedRemaining[key] || 0;
              const closedQty = Math.min(remaining, closedAvail);
              if (closedQty > 0) {
                returnClosedRemaining[key] = closedAvail - closedQty;
                remaining -= closedQty;
                rows.push({
                  ...item,
                  quantity: closedQty,
                  _baseLineItemQty: Math.max(1, toNum(item?.quantity || 1)),
                  returnLabelType: 'CLOSED',
                });
              }

              const openAvail = returnOpenRemaining[key] || 0;
              const openQty = Math.min(remaining, openAvail);
              if (openQty > 0) {
                returnOpenRemaining[key] = openAvail - openQty;
                remaining -= openQty;
                rows.push({
                  ...item,
                  quantity: openQty,
                  _baseLineItemQty: Math.max(1, toNum(item?.quantity || 1)),
                  returnLabelType: 'IN_PROGRESS',
                });
              }

              if (remaining > 0) {
                rows.push({
                  ...item,
                  quantity: remaining,
                  _baseLineItemQty: Math.max(1, toNum(item?.quantity || 1)),
                  returnLabelType: null,
                });
              }

              return rows.map((r) => generateItemHTML(r, t));
            })
            .join('')}
        </div>
      </div>
    `;
  }

  shipmentsContainer.innerHTML = shipmentsHTML;
}

function toNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

function getBaseLineItemQtyForMath(it) {
  const b = Number(it?._baseLineItemQty);
  if (Number.isFinite(b) && b > 0) return b;

  const q = Number(it?.quantity);
  return Number.isFinite(q) && q > 0 ? q : 1;
}

function countDecimals(amountStr) {
  const s = String(amountStr ?? '');
  const i = s.indexOf('.');
  return i === -1 ? 0 : (s.length - i - 1);
}

function sumDiscountAllocations(it) {
  const arr = Array.isArray(it?.discountAllocations) ? it.discountAllocations : [];
  return arr.reduce((s, d) => {
    const m = pickMoneyFromSet(d?.allocatedAmountSet);
    return s + toNum(m?.amount);
  }, 0);
}

function currencyFractionDigits(code) {
  if (code === 'JPY') return 0;
  return 2;
}

/**
 * Legacy heuristic (kept as fallback only).
 * With BE discountContext, this should rarely be needed.
 */
function isLikelyOrderLevelProration(it, currencyCode) {
  const dc = it?.discountContext || {};
  if (dc.hasItemLevelDiscount) return false; // item-level => never proration
  if (dc.hasOrderLevelDiscount && !dc.hasItemLevelDiscount) return true; // order-level => original

  const discUnit = pickMoneyFromSet(it?.discountedUnitPriceSet);
  if (!discUnit?.amount) return false;

  const qty = getBaseLineItemQtyForMath(it);
  const orig = pickMoneyFromSet(it?.originalUnitPriceSet) || {};
  const discTotal = pickMoneyFromSet(it?.discountedTotalSet);

  const origUnit = toNum(orig?.amount);
  const discLineTotal = toNum(discTotal?.amount);
  const origLineTotal = origUnit * qty;

  const allocated = sumDiscountAllocations(it);

  const shouldForceOriginal =
    orderHasPostPurchaseEvents(currentOrderData) ||
    Boolean(it?.paymentStatusRefunded) ||
    Boolean(it?.returnLabelType);

  const hasLineTotalReduction =
    discTotal && origLineTotal > 0 && discLineTotal >= 0 && discLineTotal < origLineTotal;
  if (hasLineTotalReduction) return false;

  const allowed = currencyFractionDigits(currencyCode);
  const dec = countDecimals(discUnit.amount);
  const hasWeirdDecimals = dec > allowed;

  if (shouldForceOriginal && allocated > 0 && hasWeirdDecimals) return true;
  if (hasWeirdDecimals) return true;

  if (discTotal && allocated > 0) {
    const discUnitAmt = toNum(discUnit?.amount);
    const lineTotalUnchanged = Math.abs(discLineTotal - origLineTotal) < 0.0001;
    const unitChanged = discUnitAmt !== origUnit;
    if (lineTotalUnchanged && unitChanged) return shouldForceOriginal;
  }

  return false;
}

/**
 * ✅ ABSOLUTE price pick:
 * - Gift 100% discount => show discounted unit (0)
 * - If BE says order-level ONLY => show ORIGINAL
 * - If BE says item-level => show DISCOUNTED
 * - Otherwise fallback to old heuristic (rare)
 */
function pickUnitMoneyForDisplay(it) {
  const qty = getBaseLineItemQtyForMath(it);

  const orig = pickMoneyFromSet(it?.originalUnitPriceSet) || {};
  const discUnit = pickMoneyFromSet(it?.discountedUnitPriceSet);
  const discTotal = pickMoneyFromSet(it?.discountedTotalSet);

  const currencyCode = orig.currencyCode || currentOrderCurrencyCode || 'HKD';

  const origUnit = toNum(orig?.amount);
  const discUnitAmt = toNum(discUnit?.amount);
  const discLineTotal = toNum(discTotal?.amount);
  const origLineTotal = origUnit * qty;

  // ✅ 0) 100% line discount (gift) -> ALWAYS show discounted (0) if exists
  const isFullyDiscountedLine = discTotal && discLineTotal === 0 && origLineTotal > 0;
  if (isFullyDiscountedLine && discUnit) return discUnit;

  // ✅ 1) ABSOLUTE rule from BE
  const dc = it?.discountContext || {};
  const hasOrder = Boolean(dc.hasOrderLevelDiscount);
  const hasItem = Boolean(dc.hasItemLevelDiscount);

  // order-level only => original
  if (hasOrder && !hasItem) return orig;

  // item-level => discounted (computed in BE)
  if (hasItem && discUnit) return discUnit;

  // ✅ 2) fallback heuristic (legacy)
  if (isLikelyOrderLevelProration(it, currencyCode)) return orig;

  if (discUnit && discUnitAmt !== origUnit) return discUnit;

  return orig;
}

function generateItemHTML(item, t) {
  const variant = item.variant || {};
  const sku = variant.sku || variant.magentoSku?.value || '';
  const colorFamily = variant.magentoColorFamily?.value || '';
  const variantTitle = variant.title || '';
  const size = variantTitle.split(' / ')[1] || '';
  const quantity = item.quantity || 1;

  const money = pickUnitMoneyForDisplay(item);
  const price = money.amount || 0;
  const currencyCode = money.currencyCode || currentOrderCurrencyCode || 'HKD';

  const imageUrl =
    item?.resolvedImageUrl ||
    item?.image?.url ||
    variant?.image?.url ||
    variant?.product?.featuredImage?.url ||
    variant?.product?.images?.edges?.[0]?.node?.url ||
    '';

  const labelType = item.returnLabelType; // 'IN_PROGRESS' | 'CLOSED' | null
  const returnInProgressText = t.returnInProgress || 'Return in progress';
  const returnClosedText = t.returnClosed || 'Return completed';
  const refundedLabelText = t.paymentStatusRefunded || 'Payment status: Refunded';

  const returnLabelHtml =
    labelType === 'IN_PROGRESS'
      ? `<span class="return-label">${returnInProgressText}</span>`
      : labelType === 'CLOSED'
        ? `<span class="return-label">${returnClosedText}</span>`
        : '';

  const isReturned = labelType === 'IN_PROGRESS' || labelType === 'CLOSED';
  const priceClass = `item-price${isReturned ? ' is-returned' : ''}`;

  return `
    <div class="item-row">
      ${imageUrl ? `
        <div class="item-image">
          <img src="${imageUrl}" alt="${item.title}" width="60px" height="auto"/>
        </div>
      ` : ''}
      <div class="item-details">
        <div class="item-info">
          ${returnLabelHtml}
          <h5>${item.title}</h5>
          <div class="item-specs">
            ${size ? `<span>${t.size}: ${size}</span>` : ''}
            ${colorFamily ? `<span>${t.color}: ${colorFamily}</span>` : ''}
            ${sku ? `<span>${t.sku}: ${sku}</span>` : ''}
            ${item.paymentStatusRefunded ? `<span class="refunded-label">${refundedLabelText}</span>` : ''}
          </div>
          <div class="item-quantity hide-on-desktop">
            <span>${t.qty}: ${quantity}</span>
          </div>
        </div>
      </div>
      <div class="item-quantity hide-on-mobile">
        <span>${t.qty}: ${quantity}</span>
      </div>
      <div class="${priceClass}">
        <span>${formatCurrency(price, currencyCode)}</span>
      </div>
    </div>
  `;
}

function handleContactUs() {
  window.location.href = '/pages/contact';
}

function handleTrackShipment(button) {
  const t = window.orderTrackingConfig?.translation || {};
  const url = button.dataset.url;
  const shipmentId = button.dataset.shipment;

  if (url) window.open(url, '_blank');
  else {
    const tpl = t.trackingUrlNotAvailable || 'Tracking URL not available for shipment #{{id}}';
    alert(tpl.replace('{{id}}', shipmentId || ''));
  }
}

function startNewSearch() {
  currentOrderData = null;
  orderNumber = null;
  customerEmail = null;

  const orderForm = document.getElementById('order-tracking-form');
  if (orderForm) orderForm.reset();

  const orderHeader = document.getElementById('order-header');
  const orderStatus = document.getElementById('order-status');
  const shipmentsSection = document.getElementById('shipments-section');

  if (orderHeader) orderHeader.innerHTML = '';
  if (orderStatus) orderStatus.innerHTML = '';
  if (shipmentsSection) shipmentsSection.innerHTML = '';

  document.getElementById('order-lookup-section').style.display = 'block';
  document.getElementById('order-tracking-section').style.display = 'none';

  if (heading) heading.style.display = 'block';
  window.scrollTo({ top: 0, behavior: 'smooth' });
  document.body.classList.remove('is-order-canceled');
}

function formatCurrency(amount, currencyCode = 'HKD') {
  if (amount == null) return '';
  const numAmount = typeof amount === 'string' ? Number(amount) : amount;

  const digits = currencyFractionDigits(currencyCode);
  const amountLocaleFormatted = Number(numAmount).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

  const currencyWithSymbolFormat = CURRENCY_SYMBOL_MAPPING[currencyCode] || '';
  if (currencyWithSymbolFormat) return currencyWithSymbolFormat.replace('{{amount}}', amountLocaleFormatted);

  return `${currencyCode}${amountLocaleFormatted}`;
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function shouldUseCjkYmd(locale) {
  const l = String(locale || '').toLowerCase();

  // JA
  if (l === 'ja' || l.startsWith('ja-')) return true;

  // TC: zh-tw / zh-hant (and variants)
  if (l === 'zh-tw' || l.startsWith('zh-tw') || l === 'zh-hant' || l.startsWith('zh-hant')) return true;

  // SC: zh-cn / zh-hans (and variants)
  if (l === 'zh-cn' || l.startsWith('zh-cn') || l === 'zh-hans' || l.startsWith('zh-hans')) return true;

  return false;
}

function getRuntimeLocale() {
  // best-effort: try multiple sources
  return (
    window.orderTrackingConfig?.locale ||
    window.__locale || // if you expose request.locale.iso_code
    document.documentElement?.lang ||
    navigator.language ||
    'en'
  );
}

function formatDate(dateString) {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';

  const locale = getRuntimeLocale();

  // ✅ Force yyyy年MM月dd日 for TC/SC/JA
  if (shouldUseCjkYmd(locale)) {
    const y = date.getFullYear();
    const m = pad2(date.getMonth() + 1);
    const d = pad2(date.getDate());
    return `${y}年${m}月${d}日`;
  }

  // Fallback (keep old behavior)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function showError(message) {
  const errorState = document.getElementById('error-state');
  const errorMessage = document.getElementById('error-message');
  errorMessage.textContent = message;
  errorState.style.display = 'flex';
}

function hideError() {
  document.getElementById('error-state').style.display = 'none';
}

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);